import {
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Spin,
  Typography,
  Checkbox,
  Divider,
  Button,
  Tooltip,
  Space,
  Alert,
} from 'antd';
import { useCallback, useMemo, useState } from 'react';

import { MessageInputWithFilesUpload } from '@app/components/MessageInputWithFilesUpload';
import { DEFAULT_AI_PROMPT } from '@app/components/MessageInputWithFilesUpload/data/defaultAiPrompt';
import { DEFAULT_COMPONENT_MESSAGE } from '@app/components/MessageInputWithFilesUpload/data/defaultComponentMessage';
import { MESSAGE_MODE_FIXED } from '@app/components/MessageInputWithFilesUpload/data/messageMode';
import {
  DeadlineFields,
  IncomingMessageTriggerFields,
  InstagramButtons,
  KeywordMessageRobotFields,
  SocialChannelFields,
  TagsComponent,
} from '@app/components/Modals/BotDetailsModal/components';
import {
  KEYWORD_MESSAGE_ROBOT_INDEX,
  SEND_MESSAGE_ROBOT_INDEX,
  SET_TAG_ROBOT_INDEX,
  TASK_CREATING_ROBOT_INDEX,
  TRANSITION_FROM_THE_SALES_FUNNEL_ROBOT_INDEX,
  TRANSITION_TO_THE_SALES_FUNNEL_STAGE_ROBOT_INDEX,
} from '@app/components/Modals/BotDetailsModal/constants/botIndexes';
import { SCAN_PERIOD_OPTIONS } from '@app/components/Modals/BotDetailsModal/data/scanPeriod';
import { SOCIAL_INTEGRATIONS_DICTIONARY } from '@app/components/Modals/BotDetailsModal/data/socialIntegrations';
import { useBotDetailsModal } from '@app/components/Modals/BotDetailsModal/hooks/useBotDetailsModal';
import {
  FormFields,
  type BotDetailsModalType,
  type CustomDeadlineTimeValue,
} from '@app/components/Modals/BotDetailsModal/types';
import { Schedule } from '@app/components/Schedule';
import {
  CLIENT_STATUSES,
  LOYALTY_PROGRAM_SENDOUTS,
  RULES_IS_REQUIRE,
} from '@app/constants';
import { ADD_TEXT, CANCEL, SAVE } from '@app/constants/modalText';
import { FormItem } from '@app/pages/TasksList/components';
import { getTagsSelectorData } from '@app/utils/getTagsSelectorData';

import '@app/components/Modals/BotDetailsModal/styles/BotDetailsModal.desktop.scss';
import '@app/components/Modals/BotDetailsModal/styles/BotDetailsModal.mobile.scss';

const { TextArea } = Input;
const { Text } = Typography;

const searchProps = {
  showSearch: true,
  optionFilterProp: 'label',
};

export const BotDetailsModal: BotDetailsModalType = ({
  isOpen,
  onCancel,
  onConfirm,
  title,
  isLoading,
  triggerList = [],
  salesFunnelStagesList = [],
  selectedBotTypeId = '',
  servicesList,
  onDeleteBot,
  bot,
  type,
  employees,
  socialIntegrations,
  tags,
  getTagsHandler,
  allBranches,
  selectedBotFiles,
  salesFunnelsList = [],
  getSalesFunnelStagesListHandler,
  isUploadingAttachments,
  taskUsersList,
}) => {
  const {
    getBotDetailsModalTitle,
    handleCancel,
    submit,
    isAddButtonLoading,
    handleAfterOpenChange,
    form,
    setSelectedTriggerId,
    addButtonTooltip,
    fieldsToDisplay,
    initialScheduleValues,
    handleTriggerChange,
    handleSalesFunnelChange,
    handleChannelOptions,
    handleDeadlineChange,
    selectedDeadlineItem,
    validateNumericInput,
    onCustomDayInput,
    filteredIntegrations,
    handleScheduleChange,
    showSchedule,
    handleBranchChange,
    selectedTriggerId,
    taskSelectedDeadlineItem,
    handleTaskDeadlineChange,
    tempTime,
    setTempTime,
    taskTempTime,
    setTaskTempTime,
    isRobotReplyEnabled,
    isSetTagIncomingMessageRobot,
    isNeedAddValue,
    conflictingTagIdsRules,
    resetInstagramPublicationsOnChannelChange,
    overlapCheckGateRef,
    windowSessionId,
    stopOverlapChecks,
  } = useBotDetailsModal({
    title,
    onDeleteBot,
    triggerList,
    onCancel,
    isOpen,
    isLoading,
    selectedBotTypeId,
    socialIntegrations,
    bot,
    selectedBotFiles,
    getSalesFunnelStagesListHandler,
    isUploadingAttachments,
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [taskDropdownOpen, setTaskDropdownOpen] = useState(false);
  const [customDeadlineRawValue, setCustomDeadlineRawValue] =
    useState<CustomDeadlineTimeValue>();
  const [taskCustomDeadlineRawValue, setTaskCustomDeadlineRawValue] =
    useState<CustomDeadlineTimeValue>();
  const [socialIntegrationTypeValue, setSocialIntegrationTypeValue] = useState<
    number | string | null | undefined
  >();
  const serviceCategoryOptions = useMemo(() => {
    const map = new Map<number, string>();

    servicesList.forEach((service) => {
      const { CategoryId, Category } = service as {
        CategoryId?: number;
        Category?: string;
      };

      if (
        typeof CategoryId === 'number' &&
        CategoryId > 0 &&
        Category &&
        !map.has(CategoryId)
      ) {
        map.set(CategoryId, Category);
      }
    });

    return Array.from(map.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [servicesList]);
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);
  const seconds = Array.from({ length: 60 }, (_, i) => i);
  const messageSocialType =
    typeof socialIntegrationTypeValue === 'string'
      ? socialIntegrationTypeValue
      : undefined;

  const getDisplayValue = (
    rawValue: CustomDeadlineTimeValue,
    tempTime: { h: number; m: number; s: number }
  ) => {
    if (typeof rawValue === 'string') {
      const value = rawValue.split(':');

      return value.length === 3
        ? `${value[0].padStart(2, '0')}:${value[1].padStart(
            2,
            '0'
          )}:${value[2].padStart(2, '0')}`
        : `${tempTime.h.toString().padStart(2, '0')}:${tempTime.m
            .toString()
            .padStart(2, '0')}:${tempTime.s.toString().padStart(2, '0')}`;
    }

    if (
      rawValue &&
      typeof rawValue === 'object' &&
      typeof rawValue.format === 'function'
    ) {
      return rawValue.format('HH:mm:ss');
    }

    return `${tempTime.h.toString().padStart(2, '0')}:${tempTime.m
      .toString()
      .padStart(2, '0')}:${tempTime.s.toString().padStart(2, '0')}`;
  };

  const displayValue = getDisplayValue(customDeadlineRawValue, tempTime);

  const taskDisplayValue = getDisplayValue(
    taskCustomDeadlineRawValue,
    taskTempTime
  );

  const handleModalAfterOpenChange = useCallback(
    async (open: boolean) => {
      await handleAfterOpenChange(open);

      if (!open) {
        setCustomDeadlineRawValue(undefined);
        setTaskCustomDeadlineRawValue(undefined);
        setSocialIntegrationTypeValue(undefined);
        return;
      }

      setCustomDeadlineRawValue(
        form.getFieldValue(FormFields.CustomDeadlineTime)
      );
      setTaskCustomDeadlineRawValue(
        form.getFieldValue('task_custom_deadline_time')
      );
      setSocialIntegrationTypeValue(
        form.getFieldValue('socialIntegrationType')
      );
    },
    [form, handleAfterOpenChange]
  );

  const getDropdownContent = useCallback(
    ({
      fieldName,
      setOpen,
      setTime,
      value,
      setDeadlineRawValue,
    }: {
      fieldName: string;
      setOpen(open: boolean): void;
      setTime: React.Dispatch<
        React.SetStateAction<{
          h: number;
          m: number;
          s: number;
        }>
      >;
      value: {
        h: number;
        m: number;
        s: number;
      };
      setDeadlineRawValue: React.Dispatch<
        React.SetStateAction<CustomDeadlineTimeValue>
      >;
    }) => (
      <Space>
        <Select
          value={value.h}
          onChange={(h) => setTime((prev) => ({ ...prev, h }))}
          options={hours.map((h) => ({
            value: h,
            label: h.toString().padStart(2, '0'),
          }))}
          style={{ width: 60 }}
        />

        <Select
          value={value.m}
          onChange={(m) => setTime((prev) => ({ ...prev, m }))}
          options={minutes.map((m) => ({
            value: m,
            label: m.toString().padStart(2, '0'),
          }))}
          style={{ width: 60 }}
        />

        <Select
          value={value.s}
          onChange={(s) => setTime((prev) => ({ ...prev, s }))}
          options={seconds.map((s) => ({
            value: s,
            label: s.toString().padStart(2, '0'),
          }))}
          style={{ width: 60 }}
        />

        <Button
          type="primary"
          onClick={() => {
            const nextValue = `${value.h.toString().padStart(2, '0')}:${value.m
              .toString()
              .padStart(2, '0')}:${value.s.toString().padStart(2, '0')}`;

            form.setFieldValue(fieldName, nextValue);
            setDeadlineRawValue(nextValue);
            setOpen(false);
          }}
          block
        >
          ОК
        </Button>
      </Space>
    ),
    [form, hours, minutes, seconds]
  );

  const handleDeadlineDropdownOpenChange = useCallback(
    (
      open: boolean,
      setOpen: React.Dispatch<boolean>,
      setTime: React.Dispatch<
        React.SetStateAction<{
          h: number;
          m: number;
          s: number;
        }>
      >,
      deadlineRawValue: CustomDeadlineTimeValue
    ) => {
      setOpen(open);

      if (open) {
        const rawValue = deadlineRawValue;
        const isStringRawValue = typeof rawValue === 'string';
        const isObjectValue =
          rawValue &&
          typeof rawValue === 'object' &&
          typeof rawValue.format === 'function';

        if (isStringRawValue) {
          const [h, m, s] = rawValue.split(':').map(Number);

          setTime({
            h: Number.isNaN(h) ? 0 : h,
            m: Number.isNaN(m) ? 0 : m,
            s: Number.isNaN(s) ? 0 : s,
          });
        } else if (isObjectValue) {
          setTime({
            h: rawValue.hour(),
            m: rawValue.minute(),
            s: rawValue.second(),
          });
        } else {
          setTime({ h: 0, m: 0, s: 0 });
        }
      }
    },
    []
  );

  return (
    <Modal
      title={getBotDetailsModalTitle(type)}
      open={isOpen}
      maskClosable={false}
      onCancel={handleCancel}
      onOk={submit}
      okText={type === 'create' ? ADD_TEXT : SAVE}
      cancelText={CANCEL}
      classNames={{
        body: 'bot-details-modal-body',
        header: 'bot-details-modal-header',
      }}
      okButtonProps={{
        loading: isAddButtonLoading,
        disabled: isAddButtonLoading,
      }}
      cancelButtonProps={{ disabled: isAddButtonLoading }}
      afterOpenChange={handleModalAfterOpenChange}
      afterClose={() => {
        stopOverlapChecks();
        form.resetFields();
        setCustomDeadlineRawValue(undefined);
        setTaskCustomDeadlineRawValue(undefined);
        setSocialIntegrationTypeValue(undefined);
        setSelectedTriggerId(undefined);
      }}
      footer={
        <div className="bc-bot-details-modal__footer">
          <Tooltip title={addButtonTooltip} placement="top">
            <Button
              type="primary"
              loading={isAddButtonLoading}
              disabled={isAddButtonLoading}
              onClick={submit}
              className="bc-bot-details-modal__add-btn"
            >
              {type === 'create' ? ADD_TEXT : SAVE}
            </Button>
          </Tooltip>
          <Button onClick={handleCancel} disabled={isAddButtonLoading}>
            {CANCEL}
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <Spin spinning />
      ) : (
        <>
          {fieldsToDisplay?.length ? (
            <Form
              form={form}
              onFinish={onConfirm}
              onValuesChange={(changedValues) => {
                if (FormFields.CustomDeadlineTime in changedValues) {
                  setCustomDeadlineRawValue(
                    changedValues[FormFields.CustomDeadlineTime]
                  );
                }

                if ('custom_deadline_time' in changedValues) {
                  setCustomDeadlineRawValue(
                    changedValues['custom_deadline_time']
                  );
                }

                if ('socialIntegrationType' in changedValues) {
                  setSocialIntegrationTypeValue(
                    changedValues.socialIntegrationType
                  );
                }

                resetInstagramPublicationsOnChannelChange(changedValues);
              }}
              className="bot-details-modal-form"
              initialValues={{
                message: DEFAULT_COMPONENT_MESSAGE,
                schedule: initialScheduleValues,
                description: 'Связаться',
                isNeedAdd: true,
                messageMode: MESSAGE_MODE_FIXED,
                aiPrompt: DEFAULT_AI_PROMPT,
              }}
            >
              {selectedBotTypeId !== KEYWORD_MESSAGE_ROBOT_INDEX && (
                <FormItem
                  name="trigger"
                  label="Триггер"
                  rules={[
                    {
                      required: true,
                      message: 'Пожалуйста, выберите триггер',
                    },
                  ]}
                  require
                >
                  <Select
                    placeholder="Выберите триггер"
                    options={triggerList.map(
                      ({ id, title, disabledReason }) => {
                        const handledTitle =
                          title === 'По истечению времени'
                            ? 'По истечении времени'
                            : title;

                        return {
                          value: id,
                          label: disabledReason ? (
                            <Tooltip title={disabledReason} placement="left">
                              {handledTitle}
                            </Tooltip>
                          ) : (
                            handledTitle
                          ),
                          disabled: Boolean(disabledReason),
                        };
                      }
                    )}
                    onChange={handleTriggerChange}
                  />
                </FormItem>
              )}
              {fieldsToDisplay.includes('funnel') && (
                <FormItem
                  require
                  name="funnel"
                  label="Воронка"
                  rules={[
                    {
                      required: true,
                      message: 'Пожалуйста, выберите воронку',
                    },
                  ]}
                >
                  <Select
                    placeholder="Выберите воронку"
                    options={salesFunnelsList || []}
                    onChange={handleSalesFunnelChange}
                  />
                </FormItem>
              )}

              {fieldsToDisplay.includes('funnelStage') && (
                <FormItem
                  require
                  name="funnelStage"
                  label="В этап воронки"
                  rules={[
                    { required: true, message: 'Пожалуйста, выберите этап' },
                  ]}
                >
                  <Select
                    placeholder="Выберите этап воронки"
                    options={salesFunnelStagesList.map(({ id, title }) => ({
                      value: id,
                      label: title,
                    }))}
                  />
                </FormItem>
              )}

              {selectedBotTypeId ===
                TRANSITION_FROM_THE_SALES_FUNNEL_ROBOT_INDEX && (
                <FormItem
                  require
                  name="isNeedRemovePrevFunnel"
                  valuePropName="checked"
                >
                  <Checkbox>Удалять лид из предыдущей воронки</Checkbox>
                </FormItem>
              )}

              {fieldsToDisplay.includes('status') && (
                <FormItem
                  name="status"
                  label="Статус записи"
                  rules={[
                    { required: true, message: 'Пожалуйста, выберите статус' },
                  ]}
                  require
                >
                  <Select
                    placeholder="Выберите статус записи"
                    options={CLIENT_STATUSES.map(({ label, id }) => ({
                      value: id,
                      label,
                    }))}
                  />
                </FormItem>
              )}

              {fieldsToDisplay.includes('serviceCategories') && (
                <FormItem
                  name="serviceCategories"
                  label="Категории услуг в записи"
                >
                  <Select
                    mode="multiple"
                    placeholder="Выберите категории услуг"
                    showSearch
                    optionFilterProp="label"
                    options={serviceCategoryOptions}
                  />
                </FormItem>
              )}

              {fieldsToDisplay.includes('services') && (
                <FormItem
                  name="services"
                  label="Услуги в записи"
                  rules={[
                    { required: false, message: 'Пожалуйста, выберите услуги' },
                  ]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Выберите услуги"
                    showSearch
                    optionFilterProp="label"
                    options={servicesList.map(({ Id, Name }) => ({
                      value: Id,
                      label: Name,
                    }))}
                  />
                </FormItem>
              )}

              {fieldsToDisplay.includes('searchPrompt') && (
                <FormItem
                  name="searchPrompt"
                  label="Техническое задание для поиска клиентов"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message:
                        'Техническое задание для поиска клиентов не должно быть пустым',
                    },
                  ]}
                  require
                  extra="Опишите филиал, услуги, период и другие условия. По расписанию CRM передаст задание сервису поиска и переместит найденных лидов в этот этап. Точный результат работы ИИ заранее гарантировать нельзя."
                >
                  <TextArea
                    className="bot-search-prompt-textarea"
                    rows={4}
                    maxLength={4000}
                    showCount
                    placeholder="Например: клиенты филиала «Центр», которые получали услугу «Маникюр», но не получали услугу «Педикюр» за последние 3 месяца"
                  />
                </FormItem>
              )}

              {fieldsToDisplay.includes('scanPeriod') && (
                <FormItem
                  name="scanPeriod"
                  label="Периодичность сканирования"
                  rules={[
                    {
                      required: true,
                      message:
                        'Пожалуйста, выберите периодичность сканирования',
                    },
                  ]}
                  require
                >
                  <Radio.Group options={SCAN_PERIOD_OPTIONS} />
                </FormItem>
              )}

              {fieldsToDisplay.includes('internalMovement') && (
                <FormItem
                  name="internalMovement"
                  valuePropName="checked"
                  extra={
                    fieldsToDisplay.includes('searchPrompt')
                      ? 'Если включено, лиды клиента из других воронок не перемещаются'
                      : undefined
                  }
                >
                  <Checkbox>
                    Перемещать лидов только текущей воронки продаж
                  </Checkbox>
                </FormItem>
              )}

              {fieldsToDisplay.includes('employees') && (
                <FormItem name="employees" label="Исполнители">
                  <Select
                    mode="multiple"
                    showSearch
                    optionFilterProp="label"
                    placeholder="Выберите исполнителей"
                    options={employees ?? []}
                  />
                </FormItem>
              )}

              {fieldsToDisplay.includes('socialType') && (
                <FormItem
                  name="socialType"
                  label={<Text className="label-require">Канал связи</Text>}
                  rules={[
                    {
                      required: true,
                      message: 'Пожалуйста, выберите канал связи',
                    },
                  ]}
                >
                  <Select
                    onChange={() => handleChannelOptions('socialType')}
                    placeholder="Выберите канал связи"
                    options={SOCIAL_INTEGRATIONS_DICTIONARY}
                  />
                </FormItem>
              )}

              {fieldsToDisplay.includes('tags') && (
                <TagsComponent
                  tags={tags}
                  getTagsHandler={getTagsHandler}
                  mainForm={form}
                />
              )}

              {fieldsToDisplay.includes('deadline') && (
                <DeadlineFields
                  title="Со сроком выполнения"
                  deadlineFieldName="deadline"
                  customDeadlineDayFieldName="custom_deadline_day"
                  customDeadlineTimeFieldName="custom_deadline_time"
                  selectedTriggerId={selectedTriggerId}
                  selectedDeadlineItem={selectedDeadlineItem}
                  dropdownOpen={dropdownOpen}
                  displayValue={displayValue}
                  dropdownContent={getDropdownContent({
                    fieldName: 'custom_deadline_time',
                    setOpen: setDropdownOpen,
                    setTime: setTempTime,
                    value: tempTime,
                    setDeadlineRawValue: setCustomDeadlineRawValue,
                  })}
                  setDropdownOpen={setDropdownOpen}
                  handleDeadlineChange={handleDeadlineChange}
                  handleDeadlineDropdownOpenChange={
                    handleDeadlineDropdownOpenChange
                  }
                  validateNumericInput={validateNumericInput}
                  onCustomDayInput={onCustomDayInput}
                  setTime={setTempTime}
                  deadlineRawValue={customDeadlineRawValue}
                />
              )}

              {fieldsToDisplay.includes('ignoreSchedule') && (
                <FormItem name="ignoreSchedule" valuePropName="checked">
                  <Checkbox>Игнорировать расписание</Checkbox>
                </FormItem>
              )}

              {fieldsToDisplay.includes('incomingMessage') && (
                <IncomingMessageTriggerFields
                  form={form}
                  socialIntegrationTypeValue={socialIntegrationTypeValue}
                  filteredIntegrations={filteredIntegrations}
                  handleChannelOptions={handleChannelOptions}
                />
              )}

              {fieldsToDisplay.includes('comment') && (
                <FormItem name="comment" label="Комментарий">
                  <TextArea
                    rows={4}
                    placeholder="При необходимости напишите комментарий"
                    maxLength={400}
                  />
                </FormItem>
              )}
              {selectedBotTypeId === SET_TAG_ROBOT_INDEX && (
                <>
                  <FormItem
                    name="setTagIds"
                    label="Теги"
                    require
                    rules={[
                      {
                        validator: (_, value) =>
                          Array.isArray(value) && value.length > 0
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error(
                                  'Пожалуйста, выберите хотя бы один тег'
                                )
                              ),
                      },
                    ]}
                  >
                    <Select
                      optionFilterProp="name"
                      placeholder="Выберите теги"
                      mode="multiple"
                      allowClear={true}
                      options={getTagsSelectorData({ tags })}
                    />
                  </FormItem>

                  <FormItem
                    name="isNeedAdd"
                    label="Режим работы"
                    require
                    extra={
                      isNeedAddValue === false && (
                        <Alert
                          className="set-tag-replace-warning"
                          type="warning"
                          showIcon
                          message="Робот снимет с клиента все текущие теги (в том числе, например, «VIP») и оставит только выбранные."
                        />
                      )
                    }
                  >
                    <Select
                      options={[
                        {
                          value: true,
                          label: 'Добавить к текущим тегам',
                        },
                        {
                          value: false,
                          label: 'Заменить текущие теги',
                        },
                      ]}
                    />
                  </FormItem>

                  {isSetTagIncomingMessageRobot && (
                    <FormItem
                      name="conflictingTagIds"
                      label="Перед установкой снять теги"
                      dependencies={['setTagIds']}
                      extra="Например, ставим «P1 HOT» — снимаем «P2» и «P3»."
                      rules={conflictingTagIdsRules}
                    >
                      <Select
                        optionFilterProp="name"
                        placeholder="Выберите теги"
                        mode="multiple"
                        allowClear={true}
                        options={getTagsSelectorData({ tags })}
                      />
                    </FormItem>
                  )}
                </>
              )}
              {selectedBotTypeId === TASK_CREATING_ROBOT_INDEX && (
                <>
                  <FormItem name="description" label="Постановка задачи">
                    <TextArea
                      rows={4}
                      maxLength={400}
                      placeholder="Описание задачи"
                    />
                  </FormItem>
                  <FormItem
                    rules={RULES_IS_REQUIRE}
                    name="ownerId"
                    require
                    label="Ответственный"
                  >
                    <Select options={taskUsersList || []} />
                  </FormItem>

                  <DeadlineFields
                    title="Период выполнения задачи"
                    deadlineFieldName="task_deadline"
                    customDeadlineDayFieldName="task_custom_deadline_day"
                    customDeadlineTimeFieldName="task_custom_deadline_time"
                    selectedTriggerId={selectedTriggerId}
                    selectedDeadlineItem={taskSelectedDeadlineItem}
                    dropdownOpen={taskDropdownOpen}
                    displayValue={taskDisplayValue}
                    dropdownContent={getDropdownContent({
                      fieldName: 'task_custom_deadline_time',
                      setOpen: setTaskDropdownOpen,
                      setTime: setTaskTempTime,
                      value: taskTempTime,
                      setDeadlineRawValue: setTaskCustomDeadlineRawValue,
                    })}
                    setDropdownOpen={setTaskDropdownOpen}
                    handleDeadlineChange={handleTaskDeadlineChange}
                    handleDeadlineDropdownOpenChange={
                      handleDeadlineDropdownOpenChange
                    }
                    validateNumericInput={validateNumericInput}
                    onCustomDayInput={onCustomDayInput}
                    setTime={setTaskTempTime}
                    deadlineRawValue={taskCustomDeadlineRawValue}
                  />
                </>
              )}
              {selectedBotTypeId === KEYWORD_MESSAGE_ROBOT_INDEX && (
                <>
                  <Divider className="send-message-robot-divider" />

                  <KeywordMessageRobotFields
                    form={form}
                    messageSocialType={messageSocialType}
                    socialIntegrationTypeValue={socialIntegrationTypeValue}
                    filteredIntegrations={filteredIntegrations}
                    handleChannelOptions={handleChannelOptions}
                    showAiToggle={isRobotReplyEnabled}
                    robotId={bot?.bot.Id ?? null}
                    overlapCheckGateRef={overlapCheckGateRef}
                    windowSessionId={windowSessionId}
                  />
                </>
              )}
              {selectedBotTypeId === SEND_MESSAGE_ROBOT_INDEX && (
                <>
                  <Divider className="send-message-robot-divider" />

                  <MessageInputWithFilesUpload
                    formInstance={form}
                    socialType={messageSocialType}
                    onChange={({ target: { value } }) => {
                      form.setFieldsValue({ message: value });
                    }}
                    showFiles={
                      socialIntegrationTypeValue !== LOYALTY_PROGRAM_SENDOUTS
                    }
                    showAiToggle={isRobotReplyEnabled}
                  />

                  <InstagramButtons socialType={messageSocialType} />

                  <FormItem
                    name="branch"
                    label={
                      <Text strong>
                        Филиал
                        <br />
                        <Text type="secondary">
                          Выберите, клиентам какого филиала отправлять
                          сообщения.
                        </Text>
                      </Text>
                    }
                  >
                    <Select
                      className="branch-selector"
                      placeholder="Выберите филиал"
                      mode="multiple"
                      {...searchProps}
                      options={allBranches ?? []}
                      onChange={handleBranchChange}
                      popupRender={(menu) => (
                        <div className="bot-branch-selection-buttons">
                          <Button
                            className="bot-all-branches-button"
                            type="link"
                            onClick={() =>
                              form.setFieldValue(
                                'branch',
                                (allBranches || []).map(({ value }) => value)
                              )
                            }
                          >
                            Выбрать все
                          </Button>
                          <Button
                            className="bot-unselect-all-branches-button"
                            type="link"
                            onClick={() => form.setFieldValue('branch', null)}
                          >
                            Очистить все
                          </Button>
                          {menu}
                        </div>
                      )}
                    />
                  </FormItem>

                  <SocialChannelFields
                    form={form}
                    socialIntegrationTypeValue={socialIntegrationTypeValue}
                    filteredIntegrations={filteredIntegrations}
                    handleChannelOptions={handleChannelOptions}
                  />

                  <FormItem name="useSchedule" valuePropName="checked">
                    <Checkbox onChange={handleScheduleChange}>
                      Отправлять сообщения по графику
                    </Checkbox>
                  </FormItem>

                  {showSchedule && <Schedule form={form} />}
                </>
              )}

              <FormItem
                name="afterExecutionTags"
                label="После исполнения присвоить теги"
              >
                <Select
                  optionFilterProp="name"
                  placeholder="Выберите теги"
                  mode="multiple"
                  allowClear={true}
                  options={getTagsSelectorData({ tags })}
                />
              </FormItem>

              {type === 'create' &&
                selectedBotTypeId !==
                  TRANSITION_TO_THE_SALES_FUNNEL_STAGE_ROBOT_INDEX &&
                selectedBotTypeId !== KEYWORD_MESSAGE_ROBOT_INDEX &&
                !isSetTagIncomingMessageRobot && (
                  <FormItem name="applyToExistingLeads" valuePropName="checked">
                    <Checkbox>
                      Применить робота к текущем лидам в этапе
                    </Checkbox>
                  </FormItem>
                )}
            </Form>
          ) : (
            'Не удалось отобразить данные робота'
          )}
        </>
      )}
    </Modal>
  );
};
